/* eslint-disable unicode-bom */
// src/api/profile.ts
const API_BASE: string = (process.env.REACT_APP_API_BASE || 'http://localhost:5000').replace(/\/$/, '');

// --- Types ---
export type User = {
    id: string;
    username: string;
    email?: string | null;
    phone?: string | null;
    dob?: string | null;
    displayName?: string | null;
    bio?: string | null;
    avatarUrl?: string | null;
    followersCount?: number;
    followingCount?: number;
    settings?: Record<string, unknown> | null;
    createdAt?: string;
    [k: string]: any;
};

export type ProfileResponse = {
    success: boolean;
    user: User;
    message?: string;
};

export type UsernameAvailableResponse = {
    available: boolean;
    reason?: string;
    message?: string;
};

export type UpdateResponse = {
    success: boolean;
    user: User;
    message?: string;
};

export type ExportResponse = {
    success: boolean;
    data?: any;
    url?: string;
    message?: string;
};

export type AccountSession = {
    id: string;
    ip: string;
    deviceLabel: string;
    browser: string;
    os: string;
    deviceType: 'desktop' | 'mobile' | 'tablet' | 'unknown';
    userAgent?: string;
    createdAt?: string | null;
    lastSeenAt?: string | null;
    expiresAt?: string | null;
    revokedAt?: string | null;
    current?: boolean;
    revocable?: boolean;
};

// âœ…âœ…âœ… FIXED: USE CORRECT TOKEN KEY
export function authHeaders(): Record<string, string> {
    const token = localStorage.getItem('token'); // âœ… FIXED HERE
    return token ? { Authorization: `Bearer ${token}` } : {};
}

async function handleResponse<T>(res: Response): Promise<T> {
    const text = await res.text();
    const contentType = res.headers.get('content-type') ?? '';
    let data: any = null;

    if (contentType.includes('application/json') && text) {
        try {
            data = JSON.parse(text);
        } catch {
            data = { message: text };
        }
    } else {
        data = text;
    }

    if (!res.ok) {
        const errMsg =
            (data && (data.message || data.error || data.msg)) ||
            `HTTP ${res.status} ${res.statusText}`;
        const e: any = new Error(errMsg);
        e.status = res.status;
        e.body = data;
        throw e;
    }

    return data as T;
}

// --- API functions ---

export async function getProfile(): Promise<ProfileResponse> {
    const res = await fetch(`${API_BASE}/api/profile/me`, {
        cache: 'no-store',
        headers: {
            'Content-Type': 'application/json',
            ...authHeaders(), // âœ… now sends correct token
        },
    });
    return handleResponse<ProfileResponse>(res);
}

export async function checkUsername(username: string): Promise<UsernameAvailableResponse | boolean> {
    const q = encodeURIComponent(username);
    const res = await fetch(
        `${API_BASE}/api/profile/username-available?username=${q}`,
        {
            headers: {
                'Content-Type': 'application/json',
                ...authHeaders(), // âœ… token included
            },
        }
    );
    return handleResponse<UsernameAvailableResponse | boolean>(res);
}

export async function updateProfile(payload: Record<string, any>): Promise<UpdateResponse> {
    const res = await fetch(`${API_BASE}/api/profile`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
            ...authHeaders(), // âœ… token included
        },
        body: JSON.stringify(payload),
    });
    return handleResponse<UpdateResponse>(res);
}

export async function changePassword(payload: { currentPassword: string; recoveryPhrase: string; newPassword: string }): Promise<{ success: boolean; message?: string; token?: string }> {
    const res = await fetch(`${API_BASE}/api/profile/password`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
            ...authHeaders(),
        },
        body: JSON.stringify(payload),
    });
    return handleResponse<{ success: boolean; message?: string; token?: string }>(res);
}

export async function setRecoveryPhrase(payload: { currentPassword: string; currentRecoveryPhrase?: string; recoveryPhrase: string }): Promise<{ success: boolean; message?: string; recoveryPhraseConfigured?: boolean; recoveryPhraseStatus?: 'active' | 'used' | 'missing' }> {
    const res = await fetch(`${API_BASE}/api/profile/recovery-phrase`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            ...authHeaders(),
        },
        body: JSON.stringify(payload),
        cache: 'no-store',
    });
    return handleResponse<{ success: boolean; message?: string; recoveryPhraseConfigured?: boolean; recoveryPhraseStatus?: 'active' | 'used' | 'missing' }>(res);
}

export async function exportProfile(): Promise<ExportResponse> {
    const res = await fetch(`${API_BASE}/api/profile/export`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...authHeaders(), // âœ… token included
        },
    });
    return handleResponse<ExportResponse>(res);
}

export async function deleteAccount(payload: { currentPassword: string }): Promise<{ success: boolean; message?: string }> {
    const res = await fetch(`${API_BASE}/api/profile`, {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
            ...authHeaders(), // âœ… token included
        },
        body: JSON.stringify(payload),
    });
    return handleResponse<{ success: boolean; message?: string }>(res);
}

export async function logoutAllDevices(): Promise<{ success: boolean; message?: string }> {
    const res = await fetch(`${API_BASE}/api/auth/logout-all`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...authHeaders(),
        },
    });
    return handleResponse<{ success: boolean; message?: string }>(res);
}

export async function fetchAccountSessions(): Promise<{ success: boolean; sessions: AccountSession[]; message?: string }> {
    const res = await fetch(`${API_BASE}/api/profile/sessions`, {
        headers: {
            'Content-Type': 'application/json',
            ...authHeaders(),
        },
    });
    return handleResponse<{ success: boolean; sessions: AccountSession[]; message?: string }>(res);
}

export async function revokeAccountSession(sessionId: string): Promise<{ success: boolean; message?: string; current?: boolean }> {
    const res = await fetch(`${API_BASE}/api/profile/sessions/${encodeURIComponent(sessionId)}`, {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
            ...authHeaders(),
        },
    });
    return handleResponse<{ success: boolean; message?: string; current?: boolean }>(res);
}

// --- Public profile & social ---

export type ProfileSharedNote = {
    id: string;
    title: string;
    type: 'text' | 'code' | 'list' | 'rich';
    permission: 'view' | 'edit';
    createdAt: string;
    updatedAt: string;
};

export type PublicProfileResponse = {
    success: boolean;
    user: User & { friendsCount?: number; accountVisibility?: 'public' | 'private' };
    relationship: {
        isFriend: boolean;
        isBlocked: boolean;
        outgoingRequest?: { id: string; status: string } | null;
        incomingRequest?: { id: string; status: string } | null;
        accountVisibility?: 'public' | 'private';
        canViewFullProfile?: boolean;
        currentIsPresence?: boolean;
        canMessage?: boolean;
        messagePolicy?: {
            canMessage: boolean;
            requestRequired: boolean;
            reason: 'self' | 'blocked' | 'presence' | 'no_one' | 'friends_only' | 'direct' | 'message_request';
        };
    };
    sharedNotes?: {
        withMe: ProfileSharedNote[];
        byMe: ProfileSharedNote[];
    };
    message?: string;
};

export type FriendToggleResponse = {
    success: boolean;
    action: 'added' | 'removed' | 'requested' | 'accepted' | 'cancelled';
    isFriend: boolean;
    friendshipStatus?: 'none' | 'outgoing' | 'incoming' | 'friends';
    request?: { id: string; status: string };
    message?: string;
};

export type FriendRequestResponse = {
    success: boolean;
    action: 'accepted' | 'declined';
    isFriend: boolean;
    friendshipStatus?: 'none' | 'friends';
    message?: string;
};

export type PendingFriendRequest = {
    id: string;
    status: string;
    createdAt: string;
    user: {
        id: string;
        username: string;
        displayName?: string;
        avatarUrl?: string;
    };
};

export type FriendRequestsListResponse = {
    success: boolean;
    incoming: PendingFriendRequest[];
    outgoing: PendingFriendRequest[];
};

export type BlockToggleResponse = {
    success: boolean;
    action: 'blocked' | 'unblocked';
    isBlocked: boolean;
    message?: string;
};

export type ProfileSearchUser = {
    id: string;
    username: string;
    displayName?: string;
    avatarUrl?: string;
};

export type ProfileSearchResponse = {
    success: boolean;
    users: ProfileSearchUser[];
    message?: string;
};

export async function searchProfiles(query: string): Promise<ProfileSearchUser[]> {
    const q = encodeURIComponent(query.trim());
    if (!q || q.length < 2) return [];

    const res = await fetch(`${API_BASE}/api/profile/search?q=${q}`, {
        headers: {
            'Content-Type': 'application/json',
            ...authHeaders(),
        },
    });
    const data = await handleResponse<ProfileSearchResponse>(res);
    return data.users || [];
}

export async function getPublicProfile(username: string): Promise<PublicProfileResponse> {
    const res = await fetch(`${API_BASE}/api/profile/${encodeURIComponent(username)}`, {
        headers: {
            'Content-Type': 'application/json',
            ...authHeaders(),
        },
    });
    return handleResponse<PublicProfileResponse>(res);
}

export async function toggleFriend(userId: string): Promise<FriendToggleResponse> {
    const res = await fetch(`${API_BASE}/api/profile/${userId}/friend`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...authHeaders(),
        },
    });
    return handleResponse<FriendToggleResponse>(res);
}

export async function fetchFriendRequests(): Promise<FriendRequestsListResponse> {
    const res = await fetch(`${API_BASE}/api/profile/friend-requests`, {
        headers: {
            'Content-Type': 'application/json',
            ...authHeaders(),
        },
    });
    return handleResponse<FriendRequestsListResponse>(res);
}

export async function respondFriendRequest(requestId: string, action: 'accept' | 'decline'): Promise<FriendRequestResponse> {
    const res = await fetch(`${API_BASE}/api/profile/friend-requests/${encodeURIComponent(requestId)}/respond`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...authHeaders(),
        },
        body: JSON.stringify({ action }),
    });
    return handleResponse<FriendRequestResponse>(res);
}

export async function toggleBlock(userId: string): Promise<BlockToggleResponse> {
    const res = await fetch(`${API_BASE}/api/profile/${userId}/block`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...authHeaders(),
        },
    });
    return handleResponse<BlockToggleResponse>(res);
}

// --- Kill Switch ---

export type KillSwitchResponse = {
    success: boolean;
    killSwitch: boolean;   // true = now ON, false = now OFF
    message?: string;
    user: User;
};

export async function toggleKillSwitch(): Promise<KillSwitchResponse> {
    const res = await fetch(`${API_BASE}/api/profile/killswitch`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...authHeaders(),
        },
    });
    return handleResponse<KillSwitchResponse>(res);
}
